// Considered: SearchBarAtom/DropdownAtom are composed as-is; this molecule only groups the roster's four
// server-driving controls so they can be disabled together while the first page loads.
import React, { useMemo } from 'react';
import { useTranslation } from 'localization';
import DropdownAtom from '../../atoms/Dropdown/DropdownAtom';
import SearchBarAtom from '../../atoms/SearchBar/SearchBarAtom';
import type {
  FactorRosterSort,
  FactorRosterSortDir,
  FactorRosterSourceFilter,
  FactorRosterStatusFilter,
} from '../../../types/statusFactors';
import type { UseFactorRosterResult } from './useFactorRoster';

const STATUS_FILTERS: FactorRosterStatusFilter[] = ['all', 'measured', 'not-yet-measured', 'hidden', 'broken'];
const SOURCE_FILTERS: FactorRosterSourceFilter[] = ['all', 'rule', 'measure', 'direct'];
const SORTS: Array<{ key: string; sort: FactorRosterSort; dir: FactorRosterSortDir }> = [
  { key: 'severity', sort: 'severity', dir: 'desc' },
  { key: 'nameAsc', sort: 'name', dir: 'asc' },
  { key: 'nameDesc', sort: 'name', dir: 'desc' },
  { key: 'contribution', sort: 'contribution', dir: 'desc' },
];

const STATUS_FALLBACKS: Record<FactorRosterStatusFilter, string> = {
  all: 'Any status',
  measured: 'Measured',
  'not-yet-measured': 'Not yet measured',
  hidden: 'Status hidden',
  broken: 'Contribution broken',
};
const SOURCE_FALLBACKS: Record<FactorRosterSourceFilter, string> = {
  all: 'Any source',
  rule: 'Matches the rule',
  measure: 'Measures',
  direct: 'Always included',
};
const SORT_FALLBACKS: Record<string, string> = {
  severity: 'Sort: severity',
  nameAsc: 'Sort: name (A→Z)',
  nameDesc: 'Sort: name (Z→A)',
  contribution: 'Sort: contribution',
};

type DropdownOption = { value: string; label: string };

export interface RollupRosterControlsProps {
  roster: Pick<
    UseFactorRosterResult,
    'query' | 'searchText' | 'commitSearchText' | 'setStatus' | 'setSource' | 'setSort'
  >;
  disabled?: boolean;
}

const RollupRosterControls: React.FC<RollupRosterControlsProps> = ({ roster, disabled = false }) => {
  const { t } = useTranslation();
  const { query } = roster;

  const statusOptions = useMemo(
    () => STATUS_FILTERS.map((value) => ({ value, label: t(`statusFactors.roster.filter.status.${value}`, STATUS_FALLBACKS[value]) })),
    [t]
  );
  const sourceOptions = useMemo(
    () => SOURCE_FILTERS.map((value) => ({ value, label: t(`statusFactors.roster.filter.source.${value}`, SOURCE_FALLBACKS[value]) })),
    [t]
  );
  const sortOptions = useMemo(
    () => SORTS.map(({ key, sort, dir }) => ({ value: `${sort}:${dir}`, label: t(`statusFactors.roster.sort.${key}`, SORT_FALLBACKS[key]) })),
    [t]
  );

  return (
    <div className="rollup-roster__controls" data-testid="roster-controls">
      <SearchBarAtom
        className="rollup-roster__search"
        value={roster.searchText}
        onChange={roster.commitSearchText}
        placeholder={t('statusFactors.roster.searchPlaceholder', 'Search members')}
        ariaLabel={t('statusFactors.roster.searchLabel', 'Search roster members')}
        disabled={disabled}
      />
      <DropdownAtom
        dropdownSize="compact"
        className="rollup-roster__dropdown rollup-roster__dropdown--filter"
        value={query.status}
        options={statusOptions}
        panelClassName="rollup-roster__dropdown-panel"
        onChange={(e) => roster.setStatus(e.value as FactorRosterStatusFilter)}
        disabled={disabled}
        aria-label={t('statusFactors.roster.statusFilterLabel', 'Status filter')}
        focusInputAttrs={{ 'aria-label': t('statusFactors.roster.statusFilterLabel', 'Status filter') }}
      />
      <DropdownAtom
        dropdownSize="compact"
        className="rollup-roster__dropdown rollup-roster__dropdown--filter"
        value={query.source}
        options={sourceOptions}
        panelClassName="rollup-roster__dropdown-panel"
        onChange={(e) => roster.setSource(e.value as FactorRosterSourceFilter)}
        disabled={disabled}
        aria-label={t('statusFactors.roster.sourceFilterLabel', 'Source filter')}
        focusInputAttrs={{ 'aria-label': t('statusFactors.roster.sourceFilterLabel', 'Source filter') }}
      />
      <DropdownAtom
        dropdownSize="compact"
        className="rollup-roster__dropdown rollup-roster__dropdown--sort"
        value={`${query.sort}:${query.dir}`}
        options={sortOptions}
        panelClassName="rollup-roster__dropdown-panel"
        onChange={(e) => {
          const [sort, dir] = String(e.value).split(':');
          roster.setSort(sort as FactorRosterSort, dir as FactorRosterSortDir);
        }}
        disabled={disabled}
        aria-label={t('statusFactors.roster.sortLabel', 'Sort')}
        focusInputAttrs={{ 'aria-label': t('statusFactors.roster.sortLabel', 'Sort') }}
      />
    </div>
  );
};

export default RollupRosterControls;
